import React, { useEffect, useState, useRef } from "react";
import { BiDotsVerticalRounded, BiMessageRoundedDetail } from "react-icons/bi";
import { useLocation, useNavigate } from "react-router-dom";

const PRIORITY_STYLES = {
  P0: "bg-emerald-50 text-emerald-700 border border-emerald-100",
  P1: "bg-amber-50 text-amber-700 border border-amber-100",
  P2: "bg-orange-50 text-orange-700 border border-orange-100",
  P3: "bg-rose-50 text-rose-700 border border-rose-100",
  default: "bg-slate-50 text-slate-700 border border-slate-100",
};

export function KanbanItem({ item, column, itemIndex, onDragStart, onDragOver, onDragEnd, onDrop, isDragging, onDeleteItem, selectedItem, onRemoveFromBacklog }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);

  const daysAgo = (date) => {
    if (!date) return "";
    const today = new Date();
    const diff = today - new Date(date);
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    return days === 0 ? "Today" : `${days}d ago`;
  };

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onDeleteItem(item);
    setMenuOpen(false);
  };

  const handleRemoveFromBacklog = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onRemoveFromBacklog(item);
    setMenuOpen(false);
  };

  // Handle closing the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener("mouseup", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mouseup", handleClickOutside);
    };
  }, [menuOpen]);

  const isAssociatedWithSelectedItem = selectedItem && item.module_id === selectedItem._id;

  const handleItemClick = (e) => {
    // Only process click if not dragging and not clicking on menu
    if (!isDragging && !menuRef.current?.contains(e.target)) {
      query.set("item_modal_id", item._id);
      navigate({ search: query.toString() });
    }
  };

  return (
    <div
      id={item._id}
      className={`relative group p-2.5 mb-2 bg-white rounded-md border ${
        isAssociatedWithSelectedItem ? "border-purple-400 ring-2 ring-purple-100" : "border-slate-200"
      } shadow-sm transition-all duration-200 
                ${isDragging ? "opacity-60 scale-[1.02]" : ""} cursor-grab hover:border-slate-300 hover:shadow-md`}
      draggable
      onDragStart={(e) => onDragStart(e, item._id, column.id)}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, column.id, itemIndex)}
      onClick={handleItemClick}>
      {/* Options menu */}
      <div className="absolute top-2 right-2 z-10" ref={menuRef}>
        <button
          className="p-1 text-slate-400 hover:text-slate-700 rounded-full bg-slate-100 border border-slate-200 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          onClick={handleMenuToggle}
          onMouseDown={(e) => e.stopPropagation()}>
          <BiDotsVerticalRounded size={16} />
        </button>

        {menuOpen && (
          <div className="absolute right-0 mt-1 bg-white rounded-md shadow-lg border border-slate-200 z-20">
            <ul className="py-1">
              <li>
                {item.category !== "BUG" && (
                  <button
                    className="w-full text-left px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 flex items-center whitespace-nowrap"
                    onClick={handleRemoveFromBacklog}
                    onMouseDown={(e) => e.stopPropagation()}>
                    <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Remove from backlog
                  </button>
                )}
                <button
                  className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-slate-100 flex items-center whitespace-nowrap"
                  onClick={handleDeleteClick}
                  onMouseDown={(e) => e.stopPropagation()}>
                  <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Delete
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* Title with index and position info */}
      <div className="flex items-center mb-1.5">
        <h3 className="text-sm font-medium text-slate-800 group-hover:text-blue-600 transition-colors duration-200 leading-tight line-clamp-3 overflow-hidden">
          <span className="text-xs mr-1">#{item.index}</span>- {item.title}
        </h3>
      </div>

      {/* Priority & Category badges - more compact */}
      <div className="flex flex-wrap gap-1 mb-1.5">
        {item?.priority && (
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.default}`}>
            {item.priority}
          </span>
        )}

        {item?.category && (
          <div
            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
              item.category === "FEATURE"
                ? "bg-blue-50 text-blue-700 border border-blue-100"
                : item.category === "BUG"
                ? "bg-rose-50 text-rose-700 border border-rose-100"
                : "bg-slate-50 text-slate-700 border border-slate-100"
            }`}>
            {item.category === "FEATURE" && (
              <svg className="w-2.5 h-2.5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
              </svg>
            )}
            {item.category === "BUG" && (
              <svg className="w-2.5 h-2.5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {item.category}
          </div>
        )}
        {/* Module name if available */}
        {item?.module_name && (
          <div className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
            <span className="font-medium">{item.module_name}</span>
          </div>
        )}
      </div>
      {/* Tags - more compact */}
      {item?.roadlog_tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {item.roadlog_tags.map((tag, index) => (
            <span 
              key={index} 
              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">
              {tag.name}
            </span>
          ))}
        </div>
      )}

      {/* Effort - more compact */}
      {item?.effort_tshirt_size && (
        <div className="inline-flex items-center text-xs text-slate-700 bg-slate-50 rounded-md px-1.5 py-0.5 mb-1.5 border border-slate-100">
          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="font-medium">{item.effort_tshirt_size}</span>
        </div>
      )}

      {/* Footer with users and date */}
      <div className="flex items-center justify-between mt-1.5 pt-1 border-t border-slate-100">
        {/* Users */}
        <div className="flex items-center">
          {item?.users?.length > 0 && (
            <div className="flex -space-x-1">
              {item.users.map(
                (user, idx) =>
                  user.user_avatar &&
                  idx < 3 && (
                    <img
                      key={idx}
                      className="w-5 h-5 rounded-full border-2 border-white ring-1 ring-slate-200"
                      src={user.user_avatar}
                      alt={user.user_name}
                      title={user.user_name}
                    />
                  ),
              )}
              {item.users.length > 3 && (
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 text-xs font-medium text-slate-600 border-2 border-white">
                  +{item.users.length - 3}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Comments count */}
        {item?.comments.length > 0 && (
          <div className="inline-flex ml-auto mr-1 items-center text-xs text-slate-500 bg-slate-50 rounded-md px-1.5">
            <BiMessageRoundedDetail className="w-3 h-3 mr-1" />
            <span className="font-medium">{item.comments.length}</span>
          </div>
        )}
        {/* Date at the bottom right */}
        {item?.createdAt && (
          <div className="flex items-center gap-0.5 text-xs text-slate-400">
            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            <span>{daysAgo(item.createdAt)}</span>
          </div>
        )}
      </div>

      {/* Highlight border on hover */}
      <div className={`absolute inset-0 rounded-md border-2 border-blue-500 opacity-0 group-hover:opacity-20 transition-opacity duration-200 pointer-events-none`} />

      {/* Left column color indicator */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${column.borderColor.replace("border-l-2", "")} rounded-l-md`} />
    </div>
  );
}

export default KanbanItem;
